export type AssistantFile = {
    fileId: string;
    fileName: string;
    fileSize: number;
    fileType: string;
}

export type WatchlistCategories =
    | "PEP"
    | "LAW ENFORCEMENT"
    | "OTHER BODIES"
    | "REGULATORY ENFORCEMENT"
    | "SANCTIONS"
    | "SPECIAL INTEREST CATEGORIES";

export type Config = {
    watchlist?: WatchlistCategories;
    default?: number;
    media_check_name?: number;
}

export type Assistant = {
    id: string;
    created_by: string;
    user_display_name: string;
    created_at: Date;
    assistant: string;
    title: string;
    system_prompt_message: string;
    files: AssistantFile[];
    examples: string[];
    type: string;
    short_title: string;
    welcome_message: string;
    url: string;
    attachments: boolean;
    tools?: string[];
    tool_defaults?: string[];
    userIds: string[];
    groups: string[];
    managerIds: string[];
    managerGroups: string[];
    can_edit: boolean;
    config?: Config;
    available: boolean;
};

export type AuthClaims = {
    oid: string;
    groups: string[];
}

export const MASTER_PROMPT_MAX_LENGTH = 4000;

export type UserConfig = {
    masterPrompt: string;
    masterPromptEnabled: boolean;
    maxLength?: number;
};

export interface User {
    id: string
    displayName: string
    mail: string
    objectType: "user"
    source?: "azuread" | "b2c"
}

export interface AzureGroup {
    id: string
    displayName: string
    mail?: string
    mailNickname?: string
    objectType: "group"
    source?: "azuread" | "b2c"
}

export type SearchEntity = User | AzureGroup

export type KnowledgeFileType = 'document' | 'video';

export interface UploadedFile {
    file: File;
    type: KnowledgeFileType;
}

export type Message = {
    role: string;
    content: string;
};

export type ChatStatus = {
    suggest_followup_questions?: boolean;
};

export type MessageFile = {
    fileName: string;
    fileText: string;
    newFile: boolean;
};

export type MessageMetadata = {
    thoughts?: string;
    reasoning_summary?: string;
    web_search_sources?: WebSearchSource[];
    files?: MessageFile[];
    // Earlier attachments the backend left out of this turn to fit the conversation budget.
    dropped_files?: string[] | null;
};

export type WebSearchSource = {
    url: string;
    title: string;
};

// File generated by the assistant's code interpreter, persisted to blob storage.
export type GeneratedFile = {
    filename: string;
    blob_path: string;
    mime_type: string;
    size?: number;
    is_image: boolean;
};

export type MessageContext = {
    chat_status: ChatStatus | null;
    metadata?: MessageMetadata;
    followup_questions?: string[] | null;
    web_search_sources?: WebSearchSource[] | null;
    generated_files?: GeneratedFile[] | null;
    thoughts_html?: string;
    // Set by the frontend when the user stops generation mid-answer; persisted
    // with the assistant message so the indicator survives reloads.
    stopped?: boolean;
    assistant_id?: string;
    assistant_prompt_message?: string;
    assistant_config?: Config;
};

export type Choice = {
    index: number;
    message: Message;
    context: MessageContext;
    session_state: any;
};

export type ChatResponse = {
    choices: Choice[];
};

export type ChatRequest = {
    assistant: string;
    messages: Message[];
    context: MessageContext;
    stream?: boolean;
    session_state: any;
    reasoning?: boolean;
    web_search: boolean;
};

export type ChatMessage = {
    id: string | null;
    message: Message;
    context: MessageContext;
    date: string;
};

export type Conversation = {
    id: string;
    assistant: string;
    assistant_id: string;
    title: string;
    messages: ChatMessage[];
    date: string;
    pinned?: boolean;
};

// Raw conversation document as returned by the backend `/history/list` endpoint.
export type RawConversation = {
    id: string;
    assistant: string;
    assistantId: string;
    title: string;
    updatedAt: string;
    pinned?: boolean;
};

// Raw message as returned by the backend `/history/read` endpoint.
export type RawChatMessage = {
    id: string | null;
    message: Message;
    context: MessageContext;
    createdAt: string;
};

// Raw assistant document as returned by the backend `/assistants/list` endpoint.
export type RawAssistant = {
    id: string;
    created_by: string;
    user_display_name: string;
    created_at: Date;
    assistant: string;
    title: string;
    system_prompt_message: string;
    welcome_message: string;
    files: AssistantFile[];
    examples: string[];
    type: string;
    short_title: string;
    url: string;
    attachments: boolean;
    tools?: string[];
    tool_defaults?: string[];
    userIds: string[];
    groups: string[];
    managerIds?: string[];
    managerGroups?: string[];
    can_edit?: boolean;
    config?: Config;
    available: boolean;
};

// Response body of the backend `/check-groups` endpoint.
export type CheckGroupsResponse = {
    can_create_assistants: boolean;
    is_assistant_builder_manager: boolean;
    assistant_builder_manager_group: string | null;
};

// Response body of the backend `/chat_limits` endpoint.
export type ChatUploadLimits = {
    max_file_bytes: number;
    max_file_tokens: number;
    max_files: number;
    max_total_file_tokens: number;
};

// Response body of the backend `/assistant/submit` endpoint.
export type CreateAssistantResponse = {
    id?: string;
    error?: string;
};

export type HistoryRequest = {
    conversation_id?: string;
    assistant: string;
    assistant_id: string;
    messages: Message[];
    context: MessageContext;
    user_context?: MessageContext;
    reasoning_summary?: string | null;
};

export enum CosmosDBStatus {
    NotConfigured = "CosmosDB is not configured",
    NotWorking = "CosmosDB is not working",
    Working = "CosmosDB is configured and working"
}

export type CosmosDBHealth = {
    cosmosDB: boolean;
    status: string;
};

export enum ChatHistoryLoadingState {
    Loading = "loading",
    Success = "success",
    Fail = "fail",
    NotStarted = "notStarted"
}

export enum AssistantsLoadingState {
    Loading = "loading",
    Success = "success",
    Fail = "fail",
    NotStarted = "notStarted"
}

export type SendEmailRequest = {
    subject: string;
    question: string;
}

export interface ApiResponse<T> {
    ok: boolean;
    status: number;
    data?: T;
    error?: string;
}

export type Country = {
    code: string;
    name: string;
}

export type UpdateTitleResponse = {
    conversation_id: string;
    title: string;
}

export type DeleteConversationResponse = {
    deleted_conversation_id: string
    deleted_chat_ids: string[]
}

export interface VideoEmbedTokenResponse {
    accessToken: string;
    accountId: string;
    location: string;
    videoId: string;
}